import { getCampaignImpressionData, track } from '@client/helpers/tracking';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import { getCategoryTabId } from '@client/redux/modules/mylounge/selectors';
import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
const TRACK_INTERVAL_MS = 10000;
const TRACK_IMPRESSION_LIMIT = 15;
const TRACKING_QUERY = '[id^="open-Z"],[id^="thb-Z"][aria-hidden="false"],[id^="upcoming-Z"]';

const isRectIntersecting = (intersectionRect: DOMRectReadOnly) =>
    intersectionRect.height > 0 && intersectionRect.width > 0;

export const useTrackCampaignsImpression = () => {
    const tracking = useSelector(getTrackingData);
    const categoryTabId = useSelector(getCategoryTabId);
    const isMobileScreen = useSelector(getIsMobileScreen);
    const impressionsRef = useRef([]); // use ref to persist data and avoid rerender

    const getTrackingObject = useCallback(
        (element: Element) => {
            const [source, id] = element.getAttribute('id').split('-');

            return {
                item_id: id,
                creative_slot: element.getAttribute('data-position'),
                item_name: source,
                creative_name: categoryTabId,
                visitorID: tracking?.visitorID,
            };
        },
        [categoryTabId, tracking?.visitorID],
    );

    useEffect(() => {
        let mutationObserver: MutationObserver = null;
        let mutationObserverTimeoutId: ReturnType<typeof setTimeout> = null;

        const trackingCallback = () => {
            if (impressionsRef.current.length) {
                track('view_item_list', getCampaignImpressionData(impressionsRef.current));
                impressionsRef.current = [];
            }
        };

        // Create IntersectionObserver to track campaign impressions
        const observer = new IntersectionObserver(
            entries =>
                entries.forEach(entry => {
                    if (entry.isIntersecting && isRectIntersecting(entry.intersectionRect)) {
                        impressionsRef.current = [
                            ...impressionsRef.current,
                            getTrackingObject(entry.target),
                        ];
                        observer.unobserve(entry.target);
                    }

                    if (impressionsRef.current.length === TRACK_IMPRESSION_LIMIT) {
                        trackingCallback();
                    }
                }),
            {
                root: null, // root is document
                rootMargin: '0px',
                threshold: 1.0, // trigger if 100% in viewport
            },
        );

        // throttle impression sending
        const intervalId = setInterval(trackingCallback, TRACK_INTERVAL_MS);
        window.addEventListener('beforeunload', trackingCallback);

        // give time to animation to complete
        const animateTimeoutId = setTimeout(() => {
            const campaignElements = document.querySelectorAll(TRACKING_QUERY);
            // start observing nodes
            Array.from(campaignElements).forEach(el => observer.observe(el));
        }, 600);

        // upcoming section now is lazy-loadable, so track lazy loading we use Mutation Observer and add them to Intersection Obs. list
        let upcomingAnimateTimeoutId: ReturnType<typeof setTimeout>;
        const config = {
            childList: true,
            subtree: true,
        };

        mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mut => {
                Array.from(mut.addedNodes).forEach((node: HTMLElement) => {
                    // after upcoming section is added, we need to start observing campaigns inside
                    if (node.id === 'upcoming-campaigns') {
                        upcomingAnimateTimeoutId = setTimeout(() => {
                            const upcomingCampaignElements = node.querySelectorAll(TRACKING_QUERY);
                            // start observing nodes
                            Array.from(upcomingCampaignElements).forEach(el =>
                                observer.observe(el),
                            );
                        }, 100);
                    }
                    // track lazy loaded open campaigns on mobile
                    if (node.id && node.id.startsWith('open-Z')) {
                        observer.observe(node);
                    }
                });
            });
        });
        // give animations a little bit time, might be changed with RAF
        mutationObserverTimeoutId = setTimeout(() => {
            // in mobile we use lazy load for campaigns
            if (isMobileScreen) {
                mutationObserver.observe(document.querySelector('#campaigns-open'), config);
            }
            mutationObserver.observe(
                document.querySelector('#lazyload-campaigns-upcoming'),
                config,
            );
        }, 500);

        return () => {
            if (upcomingAnimateTimeoutId) {
                clearTimeout(upcomingAnimateTimeoutId);
            }
            clearTimeout(animateTimeoutId);
            clearInterval(intervalId);
            observer.disconnect();
            window.removeEventListener('beforeunload', trackingCallback);
            if (mutationObserver) {
                mutationObserver.disconnect();
                clearTimeout(mutationObserverTimeoutId);
            }
        };
    }, [tracking, categoryTabId, isMobileScreen, getTrackingObject]);
};
